import { FC, useState, useMemo } from 'react'
import { FileText, ChevronRight, Stethoscope, ClipboardList, ListChecks, ArrowRightLeft } from 'lucide-react'
import { cn } from '../../lib/utils'
import { ChatMessage } from '../../types/chat'

interface DocumentsStripProps {
  messages: ChatMessage[]
  onDocumentClick: (message: ChatMessage) => void
  isHidden?: boolean
}

const getTypeConfig = (type: string) => {
  switch (type) {
    case 'hpi':
      return {
        bgColor: 'bg-clinical-teal/20',
        textColor: 'text-clinical-teal',
        icon: Stethoscope,
        label: 'HPI'
      }
    case 'assessment':
      return {
        bgColor: 'bg-clinical-sage/20',
        textColor: 'text-clinical-sage',
        icon: ClipboardList,
        label: 'A&P'
      }
    case 'sweep':
      return {
        bgColor: 'bg-clinical-cyan/20',
        textColor: 'text-clinical-cyan',
        icon: ListChecks,
        label: 'Sweep'
      }
    case 'ipass':
      return {
        bgColor: 'bg-clinical-amber/20',
        textColor: 'text-clinical-amber',
        icon: ArrowRightLeft,
        label: 'I-PASS'
      }
    default:
      return {
        bgColor: 'bg-primary/20',
        textColor: 'text-primary',
        icon: FileText,
        label: 'Doc'
      }
  }
}

/**
 * Vertical documents strip on right edge of chat area
 * Shows all artifacts in current conversation
 * Collapses to icon+count, expands to show list
 * Hides when artifact canvas is open
 */
export const DocumentsStrip: FC<DocumentsStripProps> = ({
  messages,
  onDocumentClick,
  isHidden = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  // Filter messages to get only those with artifacts
  const documentMessages = useMemo(() => {
    return messages
      .filter(m => m.artifact && m.role === 'assistant')
      .reverse() // Most recent first
  }, [messages])

  const documentCount = documentMessages.length

  // Don't render if hidden or no documents
  if (isHidden || documentCount === 0) {
    return null
  }

  return (
    <div
      className={cn(
        "absolute right-0 top-0 bottom-0 z-10",
        "flex flex-col",
        "bg-background/95 backdrop-blur-sm",
        "border-l border-border/50",
        "transition-all duration-200 ease-out",
        isExpanded ? "w-48" : "w-10"
      )}
    >
      {/* Toggle Button */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "flex items-center justify-center gap-2 px-2 py-3",
          "border-b border-border/50",
          "hover:bg-accent/50 transition-colors",
          "text-muted-foreground hover:text-foreground"
        )}
      >
        {isExpanded ? (
          <>
            <ChevronRight className="h-4 w-4" />
            <span className="text-xs font-medium flex-1 text-left">Documents</span>
            <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded-full">
              {documentCount}
            </span>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1">
            <FileText className="h-4 w-4" />
            <span className="text-[10px] font-medium">{documentCount}</span>
          </div>
        )}
      </button>

      {/* Document List (when expanded) */}
      {isExpanded && (
        <div className="flex-1 overflow-y-auto py-2 space-y-1">
          {documentMessages.map((msg) => {
            const config = getTypeConfig(msg.artifact?.type || 'custom')
            const Icon = config.icon

            return (
              <button
                key={msg.id}
                onClick={() => onDocumentClick(msg)}
                className={cn(
                  "w-full px-2 py-2 text-left",
                  "hover:bg-accent/50 transition-colors",
                  "flex items-center gap-2"
                )}
              >
                <div className={cn(
                  "flex-shrink-0 w-6 h-6 rounded flex items-center justify-center",
                  config.bgColor
                )}>
                  <Icon className={cn("h-3.5 w-3.5", config.textColor)} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">
                    {msg.artifact?.title || config.label}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {config.label}
                  </p>
                </div>
              </button>
            )
          })}
        </div>
      )}

      {/* Collapsed Icon List (when collapsed) */}
      {!isExpanded && (
        <div className="flex-1 overflow-y-auto py-2 flex flex-col items-center gap-2">
          {documentMessages.slice(0, 5).map((msg) => {
            const config = getTypeConfig(msg.artifact?.type || 'custom')
            const Icon = config.icon

            return (
              <button
                key={msg.id}
                onClick={() => onDocumentClick(msg)}
                title={msg.artifact?.title || config.label}
                className={cn(
                  "w-7 h-7 rounded flex items-center justify-center",
                  "hover:scale-110 transition-transform",
                  config.bgColor
                )}
              >
                <Icon className={cn("h-3.5 w-3.5", config.textColor)} />
              </button>
            )
          })}
          {documentCount > 5 && (
            <span className="text-[10px] text-muted-foreground">
              +{documentCount - 5}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
